import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { capitalize } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Box, EditPage, FuseInput, FuseSelect } from '/src/components';
import { useFilterStore, useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { DataModelType, FormErrors, UpdateUserForm, UserRole } from '/src/lib/types';

/**
 * Edit User Page
 */
export const EditProfilePage: React.FC = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const filterStore = useFilterStore();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<UpdateUserForm>();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const companies = useMemo(() => filterStore.companyListGet() ?? [], [filterStore._companyList]);
  const lenders = useMemo(() => {
    const l = filterStore.lenderListGet();
    if (Array.isArray(l)) {
      return l;
    } else if (l && l._id) {
      return [l];
    }
    return [];
  }, [filterStore._lenderList]);

  const userTypeOptions = useMemo(() => {
    const options: { label: any; value: any }[] = [];

    if (userStore.isAdmin) options.push({ label: capitalize(UserRole.Admin), value: UserRole.Admin });
    if (userStore.isAdmin || userStore.isCompany) options.push({ label: 'Company', value: UserRole.Company });
    if (userStore.isAdmin || userStore.isLender)
      options.push({ label: capitalize(UserRole.Investor), value: UserRole.Investor });

    return options;
  }, [userStore.isAdmin, userStore.isCompany, userStore.isLender]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formValues?.username) {
      errors.username = 'Username is required';
    }
    if (!formValues?.firstName) {
      errors.firstName = 'First Name is required';
    }
    if (!formValues?.lastName) {
      errors.lastName = 'Last Name is required';
    }
    if (!formValues?.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formValues?.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formValues?.roles[0]) {
      errors.role = 'User Type is required';
    }
    if (
      (formValues?.roles[0] === UserRole.Company || formValues?.roles[0] === UserRole.Investor) &&
      !formValues?.company
    ) {
      errors.company = 'Company is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the User's profile.
   *
   * @remarks
   * Redirects to the User's Detail page afterwards.
   */
  const updateUserProfile = async () => {
    if (!formValues) return;

    // Validate form before submitting
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      const response = await userStore.updateUserProfile(formValues, true);

      toast.success(toastMessages.updateUser.success);
      navigate(`/users/${response?._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.updateUser.error);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (userStore.user) {
      setFormValues(userStore.user);
    }
  }, [userStore.user]);

  /** Render **/
  return (
    <EditPage
      modelName="Edit User"
      modelType={DataModelType.User}
      onEdit={updateUserProfile}
      submitLabel="Save Profile"
    >
      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseInput
          label="Username"
          value={formValues?.username}
          updateValue={updateFormValue('username')}
          error={formErrors.username}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseInput
          label="First Name"
          value={formValues?.firstName}
          updateValue={updateFormValue('firstName')}
          error={formErrors.firstName}
          required
        />
        <FuseInput
          label="Last Name"
          value={formValues?.lastName}
          updateValue={updateFormValue('lastName')}
          error={formErrors.lastName}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseInput
          label="Email"
          type="email"
          value={formValues?.email}
          updateValue={updateFormValue('email')}
          error={formErrors.email}
          required
        />
      </Box>

      {userStore.isAdmin && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <FuseSelect
            label="User Type"
            placeholder="Select User Type"
            value={formValues?.roles[0]}
            updateValue={() => {}}
            error={formErrors.role}
            options={userTypeOptions}
            disabled
            required
          />
        </Box>
      )}

      {formValues?.roles[0] === UserRole.Company && userStore.isAdmin && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <FuseSelect
            label="User Company"
            placeholder="Select User Company"
            value={formValues?.company}
            updateValue={updateFormValue('company')}
            error={formErrors.company}
            options={(userStore.isLender ? lenders : companies).map((company) => ({
              label: company.name,
              value: company._id,
            }))}
            required
            disabled
            boxProps={{ 'aria-label': 'Select a Company to associate this user with.' }}
          />
        </Box>
      )}
    </EditPage>
  );
});
